// ** Next Import
import { Link } from 'react-router-dom'

// ** MUI Imports
import IconButton from '@mui/material/IconButton'
import Box, { BoxProps } from '@mui/material/Box'
import { styled } from '@mui/material/styles'
import Typography, { TypographyProps } from '@mui/material/Typography'

// ** Type Import
import { LayoutProps } from 'src/@core/layouts/types'

// ** Custom Icon Import
import Icon from 'src/@core/components/icon'

// ** Configs
// import themeConfig from 'src/configs/themeConfig'

interface Props {
    navHover: boolean
    collapsedNavWidth: number
    hidden: LayoutProps['hidden']
    navigationBorderWidth: number
    toggleNavVisibility: () => void
    settings: LayoutProps['settings']
    saveSettings: LayoutProps['saveSettings']
    navMenuBranding?: LayoutProps['verticalLayoutProps']['navMenu']['branding']
    menuLockedIcon?: LayoutProps['verticalLayoutProps']['navMenu']['lockedIcon']
    menuUnlockedIcon?: LayoutProps['verticalLayoutProps']['navMenu']['unlockedIcon']
}

// ** Styled Components
const MenuHeaderWrapper = styled(Box)<BoxProps>(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: theme.spacing(4.5),
    transition: 'padding .25s ease-in-out',
    minHeight: theme.mixins.toolbar.minHeight
}))

const HeaderTitle = styled(Typography)<TypographyProps>(({ theme }) => ({
    fontWeight: 600,
    lineHeight: 'normal',
    textTransform: 'uppercase',
    color: theme.palette.text.primary,
    transition: 'opacity .25s ease-in-out, margin .25s ease-in-out'
}))

const LinkStyled = styled(Link)({
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none'
})

const VerticalNavHeader = (props: Props) => {
    // ** Props
    const {
        hidden,
        navHover,
        settings,
        saveSettings,
        collapsedNavWidth,
        toggleNavVisibility,
        navigationBorderWidth,
        menuLockedIcon: userMenuLockedIcon,
        navMenuBranding: userNavMenuBranding,
        menuUnlockedIcon: userMenuUnlockedIcon
    } = props

    // ** Hooks & Vars
    //const theme = useTheme()
    const { navCollapsed } = settings

    const menuCollapsedStyles = navCollapsed && !navHover ? { opacity: 0 } : { opacity: 1 }

    const classNameImage =
        settings.mode === "light" ? "" : "image-dark"
    
    const menuHeaderPaddingLeft = () => {
        if (navCollapsed && !navHover) {
            if (userNavMenuBranding) {
                return 0
            } else {
                return (collapsedNavWidth - navigationBorderWidth - 30) / 8
            }
        } else {
            return 6
        }
    }

    const MenuLockedIcon = () => userMenuLockedIcon || <Icon icon='mdi:radiobox-marked' />

    const MenuUnlockedIcon = () => userMenuUnlockedIcon || <Icon icon='mdi:radiobox-blank' />

    return (
        <MenuHeaderWrapper className='nav-header' sx={{ pl: menuHeaderPaddingLeft() }}>
            {userNavMenuBranding ? (
                userNavMenuBranding(props)
            ) : (
                <LinkStyled to='/'>
                    <img
                        src={`/images/logo_nueva_italia.svg`}
                        height={'25'}
                        alt={`logo-nueva-italia`}
                        className={classNameImage}
                    />
                    <HeaderTitle variant='h6' sx={{ ...menuCollapsedStyles, ...(navCollapsed && !navHover ? {} : { ml: 3 }) }}>
                        Nueva Italia
                    </HeaderTitle>
                </LinkStyled>
            )}

            {hidden ? (
                <IconButton
                    disableRipple
                    disableFocusRipple
                    onClick={toggleNavVisibility}
                    sx={{ p: 0, backgroundColor: 'transparent !important' }}
                >
                    <Icon icon='mdi:close' fontSize={20} />
                </IconButton>
            ) : userMenuLockedIcon === null && userMenuUnlockedIcon === null ? null : (
                <IconButton
                    disableRipple
                    disableFocusRipple
                    onClick={() => saveSettings({ ...settings, navCollapsed: !navCollapsed })}
                    sx={{
                        p: 0,
                        color: 'text.primary',
                        backgroundColor: 'transparent !important',
                        '& svg': {
                            fontSize: '1.25rem',
                            ...menuCollapsedStyles,
                            transition: 'opacity .25s ease-in-out'
                        }
                    }}
                >
                    {navCollapsed ? MenuUnlockedIcon() : MenuLockedIcon()}
                </IconButton>
            )}
        </MenuHeaderWrapper>
    )
}

export default VerticalNavHeader
